import type { PaymentStatus } from '@gty/common';

/**
 * How ONE payment row is labelled — distinct from the booking's payment LADDER
 * (`bookings/status.ts`), which describes how much of a booking is settled.
 *
 * Extracted from `PaymentsPage` so the ledger table, the receipt view and the
 * payments list cannot label the same status differently. IN_CLEARING appearing
 * as "CLEARED" on one screen and something else on another is not a cosmetic
 * inconsistency — it is the difference between money that has arrived and money
 * that has not.
 */
export const PAYMENT_STATUS_PILL: Readonly<Record<PaymentStatus, string>> = Object.freeze({
  CLEARED: 'bg-success-bg text-success-ink',
  // Amber, never red: a cheque in clearing is a normal, successful state on its
  // way to CLEARED. Colouring it as a failure is what makes staff record the
  // payment a second time.
  IN_CLEARING: 'bg-warning/16 text-warning-ink',
  BOUNCED: 'bg-danger-bg text-danger-ink',
  REFUNDED: 'bg-neutral-200 text-neutral-700',
});

export const PAYMENT_STATUS_LABEL: Readonly<Record<PaymentStatus, string>> = Object.freeze({
  CLEARED: 'CLEARED',
  IN_CLEARING: 'IN CLEARING',
  BOUNCED: 'BOUNCED',
  REFUNDED: 'REFUNDED',
});

/**
 * One line saying what the status MEANS for the booking's balance.
 *
 * Only IN_CLEARING really needs it, and that is the point — the ledger shows an
 * in-clearing row whose running balance is identical to the row above it, and
 * without this that reads as a rendering bug rather than as the truth.
 */
export const PAYMENT_STATUS_MEANING: Readonly<Record<PaymentStatus, string | null>> = Object.freeze(
  {
    CLEARED: null,
    IN_CLEARING: 'Not yet credited — does not count toward the amount paid.',
    BOUNCED: 'The funds were returned. This does not count toward the amount paid.',
    REFUNDED: 'Money returned to the customer.',
  },
);

/**
 * Does this row move the running balance?
 *
 * Mirrors the server's `SETTLED_STATUSES`. Used only to DECIDE HOW TO EXPLAIN a
 * balance the server already computed — never to compute one.
 */
export function countsTowardPaid(status: PaymentStatus): boolean {
  return status === 'CLEARED' || status === 'REFUNDED';
}
